import './UpdateItem.css';
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { Icon } from '../../components/Icon';
import { Portrait } from '../../components/Portrait';
import { ProgressRing } from '../../components/ProgressRing';
import { UfTile } from '../../components/UfTile';
import type { Geography } from '../../contracts/static';
import { eventHour, eventRich, eventText } from '../../copy/events';
import { type Rich, richText, t } from '../../copy/strings';
import { PLACES, type PlaceKey, placeName } from '../../format/places';
import type { ElectionEvent, MapLink, ResultsView } from '../../model/types';
import { clearMapLink, navigate, setMapLink } from '../../state/actions';
import { partyClass, partyOf } from '../../theme/parties';
import { batchSeals, type EventIcon, eventIcon, eventTarget, type TargetFlags } from './model';

export interface UpdateItemProps {
  event: ElectionEvent;
  /** Vista exibida (nomes do par e dos governadores; §13.5 lê os nomes dela). */
  view: ResultsView;
  /** Nome da capital e das cidades do exterior (§13.5); null enquanto a geografia não chegou. */
  geo: Geography | null;
  /** `features.photos` (M26): sem ela, iniciais em todos os retratos e nenhuma imagem de /photos/. */
  photos: boolean;
  /** Destinos ligados (`features.abroad`, `features.governors`): item cujo destino está desligado não é botão. */
  targets?: TargetFlags;
}

/** Retrato do feed: redondo de 30 px (§7.15, §3.8). */
const PORTRAIT_PX = 30;

/** Origem do destaque no mapa (§9.11.4): só quem acendeu apaga. */
export const linkSource = (ev: Pick<ElectionEvent, 'id'>): string => `updates:${ev.id}`;

function IconCell({ icon, photos }: { icon: EventIcon; photos: boolean }) {
  let inner: ComponentChildren = null;
  if (icon.kind === 'portrait') {
    const c = icon.candidate;
    inner = <Portrait candidate={c} photo={photos ? c.photo : null} size={PORTRAIT_PX} shape="round" />;
  } else if (icon.kind === 'ring') {
    inner = <ProgressRing fraction={icon.fraction} />;
  } else if (icon.kind === 'globe') {
    inner = (
      <span class="update-item__globe">
        <Icon name="globe" size={14} />
      </span>
    );
  }
  return (
    <span class="update-item__icon" aria-hidden="true" data-icon={icon.kind}>
      {inner}
    </span>
  );
}

/** Texto rico de §13.5: nomes de candidato em `<b>` (Geist 500, `--color-fg-1`, sem quebra; §7.15). */
function RichSpans({ rich }: { rich: Rich }) {
  return (
    <>
      {rich.map((part, i) =>
        part.candidate === undefined ? (
          part.text
        ) : (
          <b key={i} class="update-item__name" data-candidate={part.candidate}>
            {part.text}
          </b>
        ),
      )}
    </>
  );
}

const PCT_AFTER_NAME = /^(\s*)(\S+%)([\s\S]*)$/;

/**
 * 2ª linha do lote (§7.15): "{1º} {x%} · {2º} {y%}" por colocação (o texto e a ordem são de §13.5); o "%" logo depois de
 * cada nome sai na cor do partido dele (`--p-text`).
 */
function Line2({ rich, view }: { rich: Rich; view: ResultsView }) {
  const out: ComponentChildren[] = [];
  let party: string | null = null;
  rich.forEach((part, i) => {
    if (part.candidate !== undefined) {
      const c = view.candidates.find((x) => x.number === part.candidate);
      party = c ? partyClass(partyOf(c)) : 'party-fallback';
      out.push(
        <b key={`n${i}`} class="update-item__name" data-candidate={part.candidate}>
          {part.text}
        </b>,
      );
      return;
    }
    const m = party === null ? null : PCT_AFTER_NAME.exec(part.text);
    if (m && party !== null) {
      out.push(m[1]);
      out.push(
        <span key={`p${i}`} class={`update-item__pct ${party}`} data-dataviz="">
          {m[2]}
        </span>,
      );
      out.push(m[3]);
      party = null;
      return;
    }
    out.push(part.text);
  });
  return <>{out}</>;
}

const isPlace = (code: string): code is PlaceKey => Object.hasOwn(PLACES, code);

/** Lote "+N seções" (§7.15): contagem, selos por Δ (3 + "+N" com mais de 4) e, embaixo, os dois por colocação. */
function BatchText({ event: ev, view }: { event: ElectionEvent; view: ResultsView }) {
  if (ev.data.type !== 'sections_batch') return null;
  const line = richText(eventRich(ev, view, 'line') ?? []);
  const m = /^(\+\S+)([\s\S]*)$/.exec(line);
  const line2 = eventRich(ev, view, 'line2');
  const { shown, more } = batchSeals(ev.data.places);
  return (
    <span class="update-item__text update-item__text--batch">
      <span class="update-item__batch" aria-hidden="true">
        <span class="update-item__line1">
          <span class="update-item__count">
            {m ? (
              <>
                <b class="update-item__plus">{m[1]}</b>
                {m[2]}
              </>
            ) : (
              line
            )}
          </span>
          {shown.length > 0 ? (
            <span class="update-item__seals">
              {shown.map((code) => (
                <span
                  key={code}
                  class="update-item__seal"
                  data-code={code}
                  title={code === 'ZZ' ? t('updates.badge.abroad') : isPlace(code) ? placeName(code) : code}
                >
                  <UfTile code={code} fill="plain" size="mini" />
                </span>
              ))}
              {more > 0 ? <span class="update-item__seal-more">{t('updates.badge.more', { N: more })}</span> : null}
            </span>
          ) : null}
        </span>
        {line2 ? (
          <span class="update-item__line2">
            <Line2 rich={line2} view={view} />
          </span>
        ) : null}
      </span>
      <span class="sr-only">{eventText(ev, view, 'sr')}</span>
    </span>
  );
}

/**
 * Item de "Últimas atualizações" (§7.15): hora · ícone · texto. Com lugar, é um `<button>` que navega para ele e, no
 * hover (mouse) e no foco, destaca a UF ou o Exterior no mapa sem navegar (F104, §9.11.4); sem lugar, um bloco de
 * texto com `tabindex="-1"` (alvo do foco do "Ver mais", §15.2.2 regra 6). O `<li>` é do `FlipList`.
 */
export function UpdateItem({ event: ev, view, geo, photos, targets }: UpdateItemProps) {
  const target = eventTarget(ev, geo, targets);
  const source = linkSource(ev);
  // item que sai da lista (ou do DOM) com o destaque aceso apaga o que acendeu
  useEffect(() => () => clearMapLink(source), [source]);

  const time = (
    <time class="update-item__time" dateTime={ev.occurred_at}>
      {eventHour(ev)}
    </time>
  );
  const icon = <IconCell icon={eventIcon(ev, view)} photos={photos} />;
  const text =
    ev.type === 'sections_batch' ? (
      <BatchText event={ev} view={view} />
    ) : (
      <span class="update-item__text">
        <RichSpans rich={eventRich(ev, view, 'line', geo) ?? []} />
      </span>
    );

  if (!target) {
    return (
      <div class="update-item" data-testid="update-item" data-type={ev.type} data-id={ev.id} tabIndex={-1}>
        {time}
        {icon}
        {text}
      </div>
    );
  }
  const link: MapLink = { kind: 'place', code: target.link };
  return (
    <button
      type="button"
      class="update-item update-item--button"
      data-testid="update-item"
      data-type={ev.type}
      data-id={ev.id}
      data-place={target.link}
      onClick={() => navigate({ office: target.office, place: target.place })}
      onPointerEnter={(e: PointerEvent) => {
        if (e.pointerType === 'mouse') setMapLink(link, source);
      }}
      onPointerLeave={() => clearMapLink(source)}
      onFocus={() => setMapLink(link, source)}
      onBlur={() => clearMapLink(source)}
    >
      {time}
      {icon}
      {text}
    </button>
  );
}
